<script>
  import { afterUpdate } from "svelte";
  import { getProduct } from "../../models/product.js";
  import { subscription } from "../../models/subscription.js";
  import { userRented } from "../../models/userRented.js";
  import {
    formatDate,
    formatPrice,
    formatTitle,
    scrollToY,
  } from "../../services/utilities.js";
  import Favorite from "../../components/Favorite.svelte";
  import PurchaseButton from "../../components/PurchaseButton.svelte";
  import PuzzlePackButton from "../../components/PuzzlePackButton.svelte";
  import SubscriptionButton from "../../components/SubscriptionButton.svelte";
  import RentalQuota from "../../components/RentalQuota.svelte";
  import RentalQuotaSubscription from "../../components/RentalQuotaSubscription.svelte";
  import Carousel from "../../components/Carousel.svelte";
  import Alert from "../../components/Alert.svelte";
  import ProductPreview from "../../components/ProductPreview.svelte";

  export let params;

  let product = null;
  let loading = true;
  let selectedProduct = null;
  let pieceCount = null;

  let breadcrumbSchema = null;

  afterUpdate(async () => {
    //TODO: figure out a better way to not trigger getProduct on these ids
    let ignore = [1, 2, 3, "events", "contact-us", "ways-to-rent", "about-us"];
    if (params.title && !params.id) {
      //TODO: we should delete this logic once google crawls the website
      let titleProduct = await getProduct(params.title);
      if (titleProduct && !titleProduct.error) {
        // Simulate an HTTP redirect:
        window.location = `/product/${params.title}/sku/${titleProduct.sku}`;
      } else {
        product = { error: "Not Found" };
      }
    } else if (
      params.id &&
      selectedProduct !== params.id &&
      !ignore.some((x) => x == params.id)
    ) {
      selectedProduct = params.id;
      product = await getProduct(params.id);
      if (!product.error) {
        let pieces = product.attributes.find((a) => a.name === "Piece Count");
        pieces ? (pieceCount = pieces.value) : (pieceCount = null);
        document.title = `${product.title}${pieceCount ? ` ${pieceCount} Pieces` : ""} | Missing Piece Puzzle Exchange`;
        breadcrumbSchema = {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.puzzleexchange.com",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: product.product_type.name,
              item: `https://www.puzzleexchange.com/${getMenuNav(product.product_type.id)}`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: product.title,
              item: `https://www.puzzleexchange.com/product/${formatTitle(product.title)}/sku/${product.sku}`,
            },
          ],
        };
        const el = document.createElement("script");
        el.type = "application/ld+json";
        el.id = "breadcrumb-schema";
        document.getElementById("breadcrumb-schema")?.remove();
        el.textContent = JSON.stringify(breadcrumbSchema);
        document.head.appendChild(el);
      }
      scrollToY(0);
    }
    loading = false;
  });

  function getMenuNav(id) {
    if (id === 1) {
      return "rent";
    } else if (id === 2) {
      return "shop";
    } else if (id === 3) {
      return "accessories";
    } else if (id === 4) {
      return "puzzle-packs";
    } else if (id === 5) {
      return "puzzle-subscriptions";
    } else if (id === 6) {
      return "gift-cards";
    }
  }

  function getImgURL(img, sz) {
    if (!img) {
      return null;
    }
    if (sz) return `${img.url}${img.filename}-${sz}.${img.extension}`;
    else return `${img.url}${img.filename}.${img.extension}`;
  }
</script>

<svelte:head>
  {#if product && !product.error}
    <meta charset="utf-8" />

    {@const productTitle = `${product.title}${pieceCount ? ` ${pieceCount} Pieces` : ""}`}
    {@const productUrl = `https://www.puzzleexchange.com/product/${formatTitle(product.title)}/sku/${product.sku}`}
    {@const productImage = getImgURL(product.images[0], "sm")}

    <link rel="canonical" href={productUrl} />

    <meta property="og:title" content={productTitle} />
    <meta property="og:type" content="website" />
    <meta property="og:url" content={productUrl} />
    <meta property="og:description" content={product.description} />
    <meta property="og:image" content={productImage} />

    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={productTitle} />
    <meta name="twitter:url" content={productUrl} />
    <meta name="twitter:description" content={product.description} />
    <meta name="twitter:image" content={productImage} />

    <meta name="description" content={product.description} />
  {/if}
</svelte:head>
{#if loading}
  <p class="text-center">
    <svg class="icon spin">
      <use href="/icons.svg#circle_notch" />
    </svg>
    Loading Product...
  </p>
{:else if !product || product.error}
  <Alert type="warning" icon="warning">
    Oh no! We could not find the requested product!
    <button class="btn btn-link" on:click={() => history.back()}>
      Go Back
    </button>
  </Alert>
{:else}
  <article class="product" itemscope itemtype="http://schema.org/Product">
    <header>
      <div class="nav-header">
        <a href="/">Home</a>
        <svg class="icon">
          <use href="/icons.svg#keyboard_arrow_right" />
        </svg>
        <!-- We have to do this so filters stick if you go back -->
        {#if window.history.length > 2}
          <a href="#" on:click={() => window.history.back()}>
            {product.product_type.name}
          </a>
        {:else}
          <a href={`/${getMenuNav(product.product_type.id)}`}>
            {product.product_type.name}
          </a>
        {/if}
      </div>
      <div class="product-type">
        <span class="badge">{product.product_type.name}</span>
        {#if product.in_stock > 0 && product.active && product.in_stock < 10}
          <span class="badge badge-danger">
            Only {product.in_stock} in stock!
          </span>
          <meta
            itemprop="availability"
            content="https://schema.org/LimitedAvailability"
          />
        {:else if product.in_stock <= 0 || !product.active}
          <span class="badge badge-danger">SOLD OUT!</span>
          <meta
            itemprop="availability"
            content="https://schema.org/OutOfStock"
          />
        {:else}
          <meta itemprop="availability" content="https://schema.org/InStock" />
        {/if}
      </div>

      <h1 class="product-title" itemprop="name">
        {product.title}
        {pieceCount ? `- ${pieceCount} Pieces` : ""}
      </h1>
    </header>

    <div class="product-media">
      <!-- If this product has no associated images -->
      {#if product.images.length == 0}
        <figure class="product-image slide fade">
          <figcaption>No Images Found</figcaption>
        </figure>
      {:else}
        <section class="img-carousel">
          <Carousel delay="5" panels={product.images}>
            <div slot="panel" let:panel>
              <img
                class="image-fill"
                itemprop="image"
                alt={`${product.title}${pieceCount ? ` ${pieceCount} piece` : ""} jigsaw puzzle${product.attributes?.find((a) => a.name === "Brand")?.value ? " by " + product.attributes.find((a) => a.name === "Brand").value : ""}`}
                style="line-height:12rem"
                src={getImgURL(panel)}
                srcset="
                {getImgURL(panel, 'sm')} 320w, {getImgURL(
                  panel,
                  'md',
                )} 640w, {getImgURL(panel, 'lg')}
                1024w"
              />
            </div>
          </Carousel>
        </section>
      {/if}
    </div>

    <!-- Product Information -->
    <div class="product-description">
      {#await $subscription}
        Loading...
      {:then}
        <RentalQuotaSubscription
          subscription={$subscription}
          clearError={subscription.clearError}
        />
        <RentalQuota showDate={true} />

        <div class="product-actions">
          {#if product.price != "0"}
            <div
              class="product-price"
              itemprop="offers"
              itemtype="http://schema.org/Offer"
              itemscope
            >
              <meta itemprop="priceCurrency" content="USD" />
              {#if product.sale_price != "0" && product.sale_price != null}
                <s>{formatPrice(product.price)}</s>
                {formatPrice(product.sale_price)}
                <meta
                  itemprop="price"
                  content={formatPrice(product.sale_price)}
                />
              {:else}
                {formatPrice(product.price)}
                <meta itemprop="price" content={formatPrice(product.price)} />
              {/if}
            </div>
          {/if}

          {#if $userRented[product.sku]}
            <div class="rented-prev">
              <span>
                Rented on {formatDate($userRented[product.sku], {
                  formatStr: "MMMM dd, yyyy",
                  showTime: false,
                })}
              </span>
              <svg class="icon">
                <use href="/icons.svg#check" />
              </svg>
            </div>
          {/if}
          <Favorite {product} showText={true} />
          <div class="product-buttons">
            <!-- if rental and there is no active subscription -->
            {#if product.product_type.name === "Puzzle (Rental)" && !$subscription.hasSubscription}
              {#if product.in_stock > 0}
                <SubscriptionButton
                  subscriptionDetails={$subscription}
                  {product}
                />
                <h3 class="h3">or</h3>
                <PuzzlePackButton {product} />
              {:else}
                <span>Sold out. Favorite and check back later.</span>
              {/if}

              <!-- if rental and there is an active subscription -->
            {:else if product.product_type.name === "Puzzle (Rental)" && $subscription.hasSubscription}
              <SubscriptionButton
                subscriptionDetails={$subscription}
                {product}
              />
            {:else}
              <PurchaseButton {product} />
            {/if}
          </div>
        </div>
      {/await}

      <div class="product-attributes">
        <h2 class="h3">Details</h2>
        <p class="product-descr" itemprop="description">
          {product.description}
        </p>
        {#if product.attributes && product.attributes.length > 0}
          <table class="table padding-sm">
            <tbody>
              <tr>
                <td>SKU</td>
                <td itemprop="sku">{product.sku}</td>
              </tr>
              {#each product.attributes as attr}
                {#if attr.value != ""}
                  {#if attr.name === "Brand"}
                    <div
                      itemprop="brand"
                      itemtype="http://schema.org/Brand"
                      itemscope
                    >
                      <meta itemprop="name" content={attr.value} />
                    </div>
                  {:else if attr.name === "Artist"}
                    <div
                      itemprop="author"
                      itemtype="http://schema.org/Person"
                      itemscope
                    >
                      <meta itemprop="name" content={attr.value} />
                    </div>
                  {/if}

                  <tr>
                    <td>{attr.name}</td>
                    <td>{attr.value}</td>
                  </tr>
                {/if}
              {/each}
            </tbody>
          </table>
        {/if}
      </div>
    </div>
    {#if product.related}
      <section>
        <h2>Related Products</h2>
        <ul class="related-products">
          {#each product.related as rp}
            <li>
              <ProductPreview product={rp} />
            </li>
          {/each}
        </ul>
      </section>
    {/if}
  </article>
{/if}

<style type="text/less">
  .rented-prev {
    height: 50px;
    span {
      font-size: 12px;
    }
    .icon {
      color: var(--col-accent-1);
      font-size: 1.25em;
      margin: 0 auto;
    }
  }

  .related-products {
    list-style: none;
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-gap: var(--spacing-sm);
    max-width: 100%;
    padding: 0;

    li {
      min-width: 12rem;
    }
  }

  @media (min-width: 600px) {
    .related-products {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  @media (min-width: 1100px) {
    .related-products {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @media (min-width: 800px) {
    .product {
      padding: var(--spacing);
    }
  }

  .product {
    display: flex;
    flex-direction: column;
    max-width: var(--content-width);
    margin: auto;
    flex-wrap: wrap;
    padding: var(--spacing);

    header {
      flex: 0 0 100%;
      padding: 0 var(--spacing-sm);
    }
  }

  .product-media {
    width: 100%;
    padding: 0;
    margin-bottom: var(--spacing-sm);
    box-sizing: border-box;
  }

  .product-description {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .product-type {
    padding: var(--spacing-sm) 0;
  }

  .product-title {
    margin: 0 0 var(--spacing-sm);
    line-height: var(--line-height-lg);
  }
  .product-descr:empty {
    display: none;
  }

  .product-actions {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    margin: -1px 0 var(--spacing-xs);
    padding: var(--spacing-xs) var(--spacing-sm);
    background: var(--col-gray-98);
    border: solid 1px var(--col-gray-90);
  }

  .product-price {
    font-size: var(--font-size-lg);
    font-weight: 700;
    margin-right: auto;
  }

  .product-attributes {
    background: var(--col-gray-98);
    border: solid 1px var(--col-gray-90);
    margin: var(--spacing-xs) 0;
    padding: var(--spacing-sm);

    h2 {
      margin: 0 0 var(--spacing-sm);
    }
  }

  figure {
    display: inline-table;
  }

  figure figcaption {
    display: table-caption;
    caption-side: bottom;
  }

  @media (min-width: 750px) {
    .product {
      flex-direction: row;
    }

    .product-media {
      width: 50%;
      padding: 0 var(--spacing-sm);
    }

    .product-description {
      width: 50%;
      max-width: 600px;
    }
  }

  .product-buttons {
    display: flex;
    flex-direction: column;
  }

  .product-buttons h3 {
    align-self: center;
    margin: var(--spacing-sm);
  }

  .gc-icon {
    color: var(--col-accent-1);
    font-size: 6rem;
    margin: 0 auto;
  }
</style>
